import { setUser } from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import {
	type LoaderFunctionArgs,
	RouterProvider,
	redirect,
} from "react-router";
import { AppConfig } from "src/configs/app-config";
import { PAGE_URLS } from "src/configs/pages-urls";
import { NEXT_URL_IDENTIFIER } from "src/hooks/use-next-url";
import authRoutes from "src/router/routes/auth.router";
import initialRoutes from "src/router/routes/initial.router";
import publicRoutes from "src/router/routes/public.router";
import type { RouterConfig } from "src/router/types";
import { useOidcUser } from "src/services/oidc-exporter";
import { sentryCreateBrowserRouter } from "src/services/sentry";

export function MainRouter({ isAuthenticated }: { isAuthenticated?: boolean }) {
	const { oidcUser } = useOidcUser();
	const queryClient = useQueryClient();

	useEffect(() => {
		if (oidcUser)
			setUser({ username: oidcUser.preferred_username ?? "none" });
	}, [oidcUser]);

	const config: RouterConfig = {
		oidcUser,
		queryClient,
	};

	AppConfig.setRouterConfig(config);

	const router = useMemo(
		() =>
			sentryCreateBrowserRouter([
				...authRoutes,
				...publicRoutes,
				...(isAuthenticated
					? initialRoutes
					: [
							{
								path: "*",
								loader: ({ request }: LoaderFunctionArgs) => {
									const url = request.url;
									return redirect(
										`${PAGE_URLS.signIn.path}?${NEXT_URL_IDENTIFIER}=${url}`,
									);
								},
							},
						]),
			]),
		[isAuthenticated],
	);

	return <RouterProvider router={router} />;
}
